import { useState, useRef, useEffect, useContext } from "react";

import TemplateModalForm from "../TemplateModalForm/TemplateModalForm";

import { TemplateContext } from "../../../model/contexts/template.context";

import "./TemplateDashboardContent.scss";

const getFilteredTemplatesByTag = (templateList, selectedTag) => {
  if (selectedTag === "All") {
    return templateList;
  }

  if (selectedTag === "Untagged") {
    return templateList.filter(
      (template) => (template.tag || "").toLowerCase() === "",
    );
  }

  return templateList.filter(
    (template) =>
      (template.tag || "").toLowerCase() === selectedTag.toLowerCase(),
  );
};

const getSortedTemplatesByOption = (templateList, selectedSortOption) => {
  const sortedTemplates = [...templateList];

  switch (selectedSortOption) {
    case "Sorted: By Ascending Name":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        currentTemplate.name
          .toLowerCase()
          .localeCompare(nextTemplate.name.toLowerCase()),
      );

    case "Sorted: By Descending Name":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        nextTemplate.name
          .toLowerCase()
          .localeCompare(currentTemplate.name.toLowerCase()),
      );

    case "Sorted: Date Created (Oldest)":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        (currentTemplate.dateCreated || "").localeCompare(
          nextTemplate.dateCreated || "",
        ),
      );

    case "Sorted: Date Created (Newest)":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        (nextTemplate.dateCreated || "").localeCompare(
          currentTemplate.dateCreated || "",
        ),
      );

    case "Sorted: Date Modified (Oldest)":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        (currentTemplate.dateModified || "").localeCompare(
          nextTemplate.dateModified || "",
        ),
      );

    case "Sorted: Date Modified (Newest)":
      return sortedTemplates.sort((currentTemplate, nextTemplate) =>
        (nextTemplate.dateModified || "").localeCompare(
          currentTemplate.dateModified || "",
        ),
      );

    case "Sorted: Task Count":
      return sortedTemplates.sort(
        (currentTemplate, nextTemplate) =>
          (nextTemplate.tasks?.length || 0) -
          (currentTemplate.tasks?.length || 0),
      );

    default:
      return sortedTemplates;
  }
};

const capitalizeFirstLetter = (text) => {
  if (!text) return "";

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const TemplateDashboardContent = () => {
  const {
    templates,
    templateLoading,
    templateError,
    addTemplate,
    editTemplate,
    removeTemplate,
  } = useContext(TemplateContext);

  const dropdownRef = useRef(null);

  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [sortOption, setSortOption] = useState("Sort By");

  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  // controls whether TemplateModalForm is in create/edit mode or view mode
  const [isTemplateFormEditMode, setIsTemplateFormEditMode] = useState(false);

  // stores the selected template when viewing/editing an existing template
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);

  const [templateTags, setTemplateTags] = useState(["All"]);
  const [selectedMenuTag, setSelectedMenuTag] = useState("All");

  const [filteredTemplates, setFilteredTemplates] = useState([]);

  useEffect(() => {
    // console.log("Current list of templates", templates);
  }, [templates]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Whenever templates change, preserve current filter + current sort
  useEffect(() => {
    createTemplateTags();

    const filteredTemplatesByTag = getFilteredTemplatesByTag(
      templates,
      selectedMenuTag,
    );

    const sortedFilteredTemplates = getSortedTemplatesByOption(
      filteredTemplatesByTag,
      sortOption,
    );

    setFilteredTemplates(sortedFilteredTemplates);
  }, [templates, selectedMenuTag, sortOption]);

  const createTemplateTags = () => {
    const uniqueTemplateTags = new Set();
    const tagList = ["All"];
    let hasUntaggedTemplates = false;

    for (const template of templates) {
      const tag = capitalizeFirstLetter(template.tag || "");

      if (!tag) {
        hasUntaggedTemplates = true;
        continue;
      }

      if (!uniqueTemplateTags.has(tag)) {
        uniqueTemplateTags.add(tag);
        tagList.push(tag);
      }
    }

    if (hasUntaggedTemplates) {
      tagList.push("Untagged");
    }

    setTemplateTags(tagList);
    return tagList;
  };

  // Selecting a tag no longer resets sortOption
  const setSelectedTemplateTag = (tag) => {
    setSelectedMenuTag(tag);
  };

  // opens modal for creating a new template
  const handleOpenTemplateModal = () => {
    setSelectedTemplateData(null);
    setIsTemplateFormEditMode(true);
    setTemplateModalOpen(true);
  };

  // opens modal for viewing an existing template first
  const handleOpenExistingTemplateModal = (template) => {
    setSelectedTemplateData(template);
    setIsTemplateFormEditMode(false);
    setTemplateModalOpen(true);
  };

  // resets modal state when closing
  const handleCloseTemplateModal = () => {
    setTemplateModalOpen(false);
    setSelectedTemplateData(null);
    setIsTemplateFormEditMode(false);
  };

  // handles both creating new templates and editing existing templates
  const handleSaveTemplate = async (templatePayload) => {
    try {
      if (templatePayload.id) {
        const templateId = templatePayload.templateID || templatePayload.id;

        await editTemplate({
          templateId,
          templatePayload,
        });

        handleCloseTemplateModal();
        return;
      }

      await addTemplate(templatePayload);

      handleCloseTemplateModal();
    } catch (error) {
      console.error("handleSaveTemplate error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await removeTemplate(templateId);

      if (
        selectedTemplateData?.id === templateId ||
        selectedTemplateData?.templateID === templateId
      ) {
        handleCloseTemplateModal();
      }
    } catch (error) {
      console.error("handleDeleteTemplate error:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error.message,
      });
    }
  };

  // Selecting sort only sets sortOption.
  // The useEffect above applies the sort and preserves it after create/edit/delete.
  const handleSortOptionSelect = (option) => {
    setSortOption(option);
    setSortDropdownOpen(false);
  };

  return (
    <div className="template-dashboard-content-container">
      <div className="template-dashboard-header">
        <h2>Template Library</h2>
        <h3>Your Processes, Routines, & Checklists</h3>
      </div>

      <div className="template-dashboard-menu">
        <div className="template-dashboard-left-side">
          <div className="template-dashboard-filter-label-container">
            <div className="template-dashboard-filter-label">
              Filter By Label:
            </div>

            <div className="template-dashboard-tag-strip">
              {templateTags.map((tag) => (
                <div
                  key={tag}
                  className={`template-dashboard-tag-chip ${
                    selectedMenuTag === tag ? "active" : ""
                  }`}
                  onClick={() => setSelectedTemplateTag(tag)}
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="template-dashboard-right-side">
          <div className="template-dashboard-sort-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="template-dashboard-menu-action-button template-dashboard-sort-button"
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
            >
              {sortOption}
            </button>

            {sortDropdownOpen && (
              <div className="template-dashboard-sort-dropdown-menu">
                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Ascending Name")
                  }
                >
                  Name (A-Z)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: By Descending Name")
                  }
                >
                  Name (Z-A)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Oldest)")
                  }
                >
                  Date Created (Oldest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Created (Newest)")
                  }
                >
                  Date Created (Newest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Oldest)")
                  }
                >
                  Date Modified (Oldest)
                </div>

                <div
                  onClick={() =>
                    handleSortOptionSelect("Sorted: Date Modified (Newest)")
                  }
                >
                  Date Modified (Newest)
                </div>

                <div
                  onClick={() => handleSortOptionSelect("Sorted: Task Count")}
                >
                  Task Count
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="template-dashboard-menu-action-button template-dashboard-new-button"
            onClick={handleOpenTemplateModal}
          >
            New Template
          </button>
        </div>
      </div>

      {templateError && (
        <div className="template-dashboard-error-message">{templateError}</div>
      )}

      {templateLoading && (
        <div className="template-dashboard-loading-message">
          Loading templates...
        </div>
      )}

      <div className="template-dashboard-list-feed-section">
        {filteredTemplates.length === 0 ? (
          <div className="template-dashboard-empty-state">
            <h4>No templates yet</h4>
            <p>Create your first reusable checklist, routine, or process</p>

            <button
              type="button"
              className="template-dashboard-empty-state-button"
              onClick={handleOpenTemplateModal}
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className="template-dashboard-card-grid">
            {filteredTemplates.map((template) => {
              const templateId = template.templateID || template.id;

              return (
                <div
                  className="template-dashboard-template-card"
                  key={templateId}
                >
                  <div className="template-dashboard-template-card-header">
                    <div>
                      <h4>{template.name}</h4>

                      {template.description && <p>{template.description}</p>}

                      {template.tag?.trim() && (
                        <div className="template-dashboard-template-card-tag">
                          {capitalizeFirstLetter(template.tag)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="template-dashboard-template-card-meta">
                    <span>{template.tasks?.length || 0} tasks</span>
                  </div>

                  <div className="template-dashboard-template-card-preview">
                    {template.tasks?.length > 0 ? (
                      template.tasks.map((task, index) => (
                        <div
                          className="template-dashboard-template-card-task"
                          key={task.taskID || index}
                        >
                          <span>{index + 1}</span>
                          <p>{task.title}</p>
                        </div>
                      ))
                    ) : (
                      <div className="template-dashboard-template-card-empty-task">
                        No tasks added yet
                      </div>
                    )}
                  </div>

                  <div className="template-dashboard-template-card-actions">
                    <button
                      type="button"
                      className="template-dashboard-template-card-action-button template-dashboard-template-card-edit-button"
                      onClick={() => handleOpenExistingTemplateModal(template)}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="template-dashboard-template-card-action-button template-dashboard-template-card-delete-button"
                      onClick={() => handleDeleteTemplate(templateId)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {templateModalOpen && (
        <TemplateModalForm
          onClose={handleCloseTemplateModal}
          onSave={handleSaveTemplate}
          selectedTemplateData={selectedTemplateData}
          isFormEditMode={isTemplateFormEditMode}
          setIsFormEditMode={setIsTemplateFormEditMode}
        />
      )}
    </div>
  );
};

export default TemplateDashboardContent;
